import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Heart, 
  User, 
  Calendar, 
  CreditCard,
  XCircle,
  Loader2,
  ChevronRight,
  Star
} from 'lucide-react';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



function SubscriptionCard({ subscription, onCancel }) {
  const [canceling, setCanceling] = useState(false);
  
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? You will lose access to subscriber benefits.')) {
      return;
    }
    
    setCanceling(true);
    try {
      const result = await onCancel(subscription.subscription_id);
      if (result) {
        toast.success('Subscription canceled');
      }
    } finally {
      setCanceling(false);
    }
  };

  const statusColors = {
    active: 'bg-green-500/10 text-green-400 border-green-500/30',
    canceled: 'bg-red-500/10 text-red-400 border-red-500/30',
    past_due: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
  };

  return (
    <Card className="p-4 bg-card/50 border border-border">
      <div className="flex items-start gap-4">
        {/* Artist Avatar */}
        <Link to={`/artist/${subscription.artist_id}`}>
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            {subscription.artist?.avatar_url ? (
              <img 
                src={subscription.artist.avatar_url} 
                alt={subscription.artist.stage_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                <User className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
          </div>
        </Link>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link to={`/artist/${subscription.artist_id}`} className="font-semibold hover:text-primary">
              {subscription.artist?.stage_name || 'Artist'}
            </Link>
            <Badge variant="outline" className={statusColors[subscription.status] || ''}>
              {subscription.status}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3" />
              {subscription.tier_id} tier
            </span>
            <span className="flex items-center gap-1">
              <CreditCard className="w-3 h-3" />
              ${(subscription.artist_payout_cents + subscription.platform_fee_cents) / 100}/mo
            </span>
          </div>
          
          {subscription.current_period_end && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {subscription.status === 'active' ? 'Renews' : 'Expires'}: {new Date(subscription.current_period_end).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Link to={`/artist/${subscription.artist_id}`}>
            <Button variant="outline" size="sm">
              View <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
          {subscription.status === 'active' && (
            <Button 
              variant="ghost" 
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={handleCancel}
              disabled={canceling}
            >
              {canceling ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" />
                  Cancel
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function MySubscriptionsPage() {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptions();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API}/fan-subscriptions/my-subscriptions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (subscriptionId) => {
    try {
      const response = await fetch(`${API}/fan-subscriptions/cancel/${subscriptionId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        // Update local state
        setSubscriptions(prev => prev.map(s => 
          s.subscription_id === subscriptionId 
            ? { ...s, status: 'canceled' }
            : s
        ));
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel subscription');
        return false;
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
      return false;
    }
  };

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
  const pastSubscriptions = subscriptions.filter(s => s.status !== 'active');

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <StageEmptyState
            variant="default"
            customHeadline="Sign in to view your subscriptions"
            customHint="Track and manage your artist subscriptions"
            customAction={
              <Link to="/login">
                <Button>Sign In</Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="my-subscriptions-page">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Heart className="w-8 h-8 text-red-400" />
            My Subscriptions
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your artist channel subscriptions
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-16 h-16 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : subscriptions.length === 0 ? (
          <StageEmptyState
            variant="default"
            customHeadline="No subscriptions yet"
            customHint="Subscribe to your favorite artists to unlock exclusive content and perks!"
            customAction={
              <Link to="/home">
                <Button>Discover Artists</Button>
              </Link>
            }
          />
        ) : (
          <>
            {/* Active Subscriptions */}
            {activeSubscriptions.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Active Subscriptions ({activeSubscriptions.length})
                </h2>
                <div className="space-y-3">
                  {activeSubscriptions.map(sub => (
                    <SubscriptionCard 
                      key={sub.subscription_id} 
                      subscription={sub}
                      onCancel={handleCancel}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Past Subscriptions */}
            {pastSubscriptions.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-muted-foreground">
                  Past Subscriptions ({pastSubscriptions.length})
                </h2>
                <div className="space-y-3 opacity-70">
                  {pastSubscriptions.map(sub => (
                    <SubscriptionCard 
                      key={sub.subscription_id} 
                      subscription={sub}
                      onCancel={handleCancel}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
